import { useRef } from "react";

import { cn } from "@/utils/cn";

export interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  "aria-label"?: string;
}

/**
 * docs/ui/design-system.md §9/§11: 6× 48×48px boxes, auto-advance focus,
 * filled box gets a primary border. Not a shadcn registry item — built by
 * hand since BazaarX's OTP semantics (single controlled string value,
 * digit-only, auto-advance/auto-back, paste-the-whole-code support) don't
 * map onto any existing primitive.
 */
export function OtpInput({
  length = 6,
  value,
  onChange,
  disabled = false,
  hasError = false,
  "aria-label": ariaLabel = "One-time passcode",
}: OtpInputProps) {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? "");

  function setDigit(index: number, digit: string) {
    const next = digits.slice();
    next[index] = digit;
    onChange(next.join(""));
  }

  function handleChange(index: number, rawInput: string) {
    const digitsOnly = rawInput.replace(/\D/g, "");

    if (digitsOnly.length > 1) {
      // Pasting the whole code into one box — distribute it from here on.
      const next = value.split("");
      for (let i = 0; i < digitsOnly.length && index + i < length; i++) {
        next[index + i] = digitsOnly[i] ?? "";
      }
      const merged = next.join("").slice(0, length);
      onChange(merged);
      const nextFocusIndex = Math.min(index + digitsOnly.length, length - 1);
      inputRefs.current[nextFocusIndex]?.focus();
      return;
    }

    setDigit(index, digitsOnly);
    if (digitsOnly && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  return (
    <div role="group" aria-label={ariaLabel} className="flex gap-2">
      {digits.map((digit, index) => (
        <input
          // Index as key is correct here: these are fixed-length,
          // position-addressed boxes, not a reorderable list.
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={length}
          disabled={disabled}
          value={digit}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          aria-label={`Digit ${index + 1} of ${length}`}
          className={cn(
            "h-12 w-12 rounded-md border text-center text-h3 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            hasError ? "border-destructive" : digit ? "border-primary" : "border-input",
          )}
        />
      ))}
    </div>
  );
}
